import { useCallback, useEffect, useRef } from "react";
import type { Channel } from "@hybrid-iptv/types";
import { useChannels } from "../epg/hooks.js";
import { activeProviderSourceId, useActiveProviderSourceId } from "../epg/api.js";
import { mirrorKeyFor } from "../library/mirrorResolver.js";
import { useRemoteActions } from "../remote/remoteActions.js";
import { usePlayerStore } from "./playerStore.js";
import { arrowsBelongToBar, noteZap } from "./zapRule.js";

/** How long the channel keys must be quiet before a zap tunes: the Live page's value. */
const ZAP_SETTLE_MS = 450;

/** Where the playing channel sits in `channels`: by stream, then by name; -1 when absent. */
export function currentChannelIndex(channels: readonly Channel[], source: { url: string; title?: string } | null): number {
  if (!source) return -1;
  const byUrl = channels.findIndex((channel) => channel.streamUrl === source.url);
  if (byUrl >= 0) return byUrl;
  return source.title ? channels.findIndex((channel) => channel.name === source.title) : -1;
}

/** One step through `channels` from `from` (-1: not in the list), wrapping at both ends. */
export function stepChannel(count: number, from: number, next: boolean): number {
  if (count === 0) return -1;
  if (from < 0) return next ? 0 : count - 1;
  return next ? (from + 1) % count : (from - 1 + count) % count;
}

/** The Live page is behind the player: it changes channel itself, in guide order, with its card. */
function guideIsBehind(): boolean {
  return document.querySelector("[data-guide]") !== null;
}

/**
 * Up/Down and CH+/CH− change the channel in full screen wherever the channel
 * was started. The Live page does it in guide order; live TV started from
 * Home, My List, Search or Social had nothing listening, so Up/Down only woke
 * the bar with no control highlighted: a dead press in full screen (audit,
 * 2026-09-28). This steps through the provider's channels in number order and
 * stands aside whenever the guide is behind the player.
 *
 * Mounted only while live TV fills the screen, so nothing loads the channel
 * list for it at any other time. `onPending` names the channel a zap is
 * heading to, before it tunes, so the bar can show it; null when it lands.
 */
export function FullscreenZap({ onPending }: { onPending: (name: string | null) => void }) {
  const sourceId = useActiveProviderSourceId();
  const { data } = useChannels(sourceId);
  const channels = data?.channels ?? [];
  const channelsRef = useRef(channels);
  channelsRef.current = channels;
  const onPendingRef = useRef(onPending);
  onPendingRef.current = onPending;
  const timer = useRef<number | null>(null);
  // Where a burst of presses has got to, so each press steps from the last one.
  const target = useRef(-1);

  const zap = useCallback((next: boolean): boolean => {
    if (guideIsBehind()) return false;
    const list = channelsRef.current;
    if (list.length === 0) return false;
    const from = timer.current !== null ? target.current : currentChannelIndex(list, usePlayerStore.getState().source);
    const index = stepChannel(list.length, from, next);
    if (index < 0) return false;
    noteZap();
    target.current = index;
    onPendingRef.current(list[index]!.name);
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      timer.current = null;
      const channel: Channel | undefined = channelsRef.current[target.current];
      onPendingRef.current(null);
      if (!channel) return;
      const store = usePlayerStore.getState();
      store.pushRecent({
        id: channel.id,
        name: channel.name,
        number: channel.number,
        logoUrl: channel.logoUrl,
        streamUrl: channel.streamUrl,
        altStreamUrls: channel.altStreamUrls,
      });
      void store
        .playSource({
          url: channel.streamUrl,
          type: "live",
          title: channel.name,
          altUrls: channel.altStreamUrls,
          mirrorKey: mirrorKeyFor(activeProviderSourceId(), channel.id),
        })
        // A channel that will not load shows its error on the bar (Retry);
        // nothing else is left to do with the rejection.
        .catch(() => undefined);
    }, ZAP_SETTLE_MS);
    return true;
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const channelKey = event.key === "ChannelUp" || event.key === "ChannelDown";
      if (event.key !== "ArrowUp" && event.key !== "ArrowDown" && !channelKey) return;
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      // With a control highlighted on the bar, the arrows move between the
      // controls (components/spatialNav.ts); CH+/CH− still zap.
      const target = event.target;
      if (!channelKey && target instanceof HTMLElement && target !== document.body) return;
      // The bar is up: Up/Down go into it, they do not change the channel (zapRule.ts).
      if (!channelKey && arrowsBelongToBar(event)) return;
      // Down = next, as on the Live page and a remote's CH+.
      if (zap(event.key === "ArrowDown" || event.key === "ChannelUp")) event.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zap]);

  useRemoteActions({
    channelNext: () => zap(true),
    channelPrev: () => zap(false),
  });

  // A zap still waiting when full screen ends is dropped, and its name cleared.
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
      onPendingRef.current(null);
    },
    [],
  );

  return null;
}
